/**
 * 安全周报页面
 */
import React, { useRef } from 'react';
import { Col, Layout, Row } from 'tea-component';
import ContentBody from '@op/components/ContentBody';
import CardLoading from '@op/components/CardLoading';
import { FilterFixed, useFilterDataHook } from '@op/components/FilterCommon';
import { PAGE_NAME } from '@op/constants/page';
import { useFilterTopic } from '@op/hooks/useFilterTopic';
import moment from 'moment';
import OverviewCard from './components/OverviewCard';
import KeyMetricsChart from './components/KeyMetricsChart';
import QuadrantChart from './components/QuadrantChart';
import TrendAnalysis from './components/TrendAnalysis';
import DimensionTrend from './components/DimensionTrend';
import DetailTable from './components/DetailTable';
import { connect } from 'umi';
import useLanguage from '@op/hooks/useLanguage';
import { DBO_TM } from '@op/utils/format';
import { SecurityWeeklyReportContext } from './context';
import { goStatsDetailAction } from '@op/pages/stats/Detail';
import { useInitBuriedPointInfo } from '@op/operationLog/buriedPoint';
import { SecurityWeeklyReportPoint } from '@op/operationLog/pages';
import './index.scss';

const { Content } = Layout;

interface IWeeklyReportProps {
  gameInfo: any;
  wholeGameInfo: any;
}

const WeeklyReport: React.FC<IWeeklyReportProps> = ({
  gameInfo,
  wholeGameInfo,
}) => {
  const { formatMessage } = useLanguage();
  const contentRef = useRef<HTMLDivElement>(null);
  useInitBuriedPointInfo({ buriedPoint: SecurityWeeklyReportPoint });
  const { scrollBody, loading, filter, changeFilter, filterOptions } =
    useFilterDataHook({
      gameInfo,
      pageName: 'PUBGM_Weekly_Report',
    });

  // 请求分类数据用于 Category Tab
  const {
    data: categoryData,
    loading: filterTopicLoading,
    getSubcategoriesByCategory,
  } = useFilterTopic(gameInfo);

  /**
   * 跳转到 Feeds 页面
   * @param category 一级分类
   * @param subCategory 二级分类
   * @param extraFilter 其他参数：
   *
   * categories 参数结构：
   * - 全选时：categories: [{ category: "99999", sub_categories: [] }] 或 categories: []
   * - 一级话题全选：categories: [{ category: "hack", sub_categories: [] }]
   * - 部分选中：categories: [{ category: "hack", sub_categories: ["esp"] }]
   */
  const toFeeds = (
    category?: string,
    subCategory?: string,
    extraFilter: Record<string, any> = {},
  ) => {
    const { categories, ...filterData } = filter;

    // 构建 URL 查询参数
    const queryParams = new URLSearchParams();
    queryParams.set('_hasCategoryTab', 'true');

    // 获取当前 tab（默认 socialMedia）
    const queryString = queryParams.toString();
    const extra = queryString ? `&${queryString}` : '';
    const path = `/opinion/Feeds/Feeds?tab=socialMedia${extra}`;

    // 构建跳转参数
    const params: Record<string, any> = {
      ...filterData,
      ...extraFilter,
    };

    let data: any = null;

    if (category) {
      data = { category: category.toLocaleLowerCase() };
      if (subCategory) {
        data = { ...data, sub_categories: [subCategory.toLocaleLowerCase()] };
      } else {
        // 存在一级，不存在二级，那么就找到一级下的所有二级
        const allSubcategories = getSubcategoriesByCategory(category);
        data = { ...data, sub_categories: allSubcategories };
      }
    }
    // 使用 goStatsDetailAction 跳转，将 filter 参数存储到 sessionStorage
    goStatsDetailAction({
      path,
      params: { ...params, categories: data ? [data] : categories },
    });
  };

  return (
    <Content className="dbo-page dbo-security-weekly-report" ref={scrollBody}>
      <ContentBody
        loading={filterTopicLoading}
        pageName={PAGE_NAME.SECURITY_WEEKLY_REPORT}
        contentHeard={
          <Content.Header
            className="dbo-header"
            title={formatMessage({ id: PAGE_NAME.SECURITY_WEEKLY_REPORT })}
          />
        }
      >
        <FilterFixed
          scrollBody={scrollBody}
          filter={filterOptions}
          hideTop={loading ? 150 : 200}
          operateShowOptions={[]}
          defaultConfig={{
            date: {
              dateTypeList: ['date', 'week'],
              disabledTips: '选取了禁用时间',
              disabledDate: {
                date: (date: moment.Moment) => {
                  // 禁用 2026-01-01 之前的日期
                  return date.isAfter(moment('2025-12-31'), 'day');
                },
                week: (date: moment.Moment) => {
                  // 禁用 2026-01-01 之前的日期
                  return date.isAfter(moment('2025-12-31'), 'day');
                },
              },
            },
            topic: {
              hasCategoryTab: true,
              categoryOptions: categoryData,
              onlyCategory: true,
            },
          }}
          isTile
          defaultValue={{ ...DBO_TM.utcFilterRangeWeek(), date_type: 'weekly' }}
          confirm={(value: any) => {
            const { topic, topic_rule, ...data } = value;

            changeFilter({ ...data, ...gameInfo });
          }}
        />
        <CardLoading loading={loading}>
          <SecurityWeeklyReportContext.Provider value={{ toFeeds }}>
            <div ref={contentRef} className="dbo-weekly-report__content">
              {/* 核心概览：AI 总结*/}
              <OverviewCard
                filter={filter}
                gameInfo={gameInfo}
                wholeGameInfo={wholeGameInfo}
              />

              {/* 总标题 */}
              <h2 className="dbo-key-metrics-section__title">
                {formatMessage({ id: 'Key Metrics' })}
              </h2>
              {/* 关键指标趋势图 */}
              <KeyMetricsChart filter={filter} />

              {/* 四象限散点图 */}
              <QuadrantChart filter={filter} />

              {/* 趋势分析模块：分举报类型/子话题/关键词 */}
              <div className="dbo-weekly-report__section">
                <TrendAnalysis filter={filter} />
              </div>

              {/* 分语言 + 分渠道趋势 */}
              <div className="dbo-weekly-report__section">
                <Row gap={20} className="dbo-dimension-trend-row">
                  <Col>
                    <DimensionTrend filter={filter} dimension="language" />
                  </Col>
                  <Col>
                    <DimensionTrend filter={filter} dimension="channel" />
                  </Col>
                </Row>
              </div>

              {/* 多维度明细表 */}
              <div className="dbo-weekly-report__section">
                <DetailTable filter={filter} />
              </div>
            </div>
          </SecurityWeeklyReportContext.Provider>
        </CardLoading>
      </ContentBody>
    </Content>
  );
};

export default connect((state: any) => {
  const { opinionCommon } = state;
  return {
    gameInfo: opinionCommon.gameInfo,
    wholeGameInfo: opinionCommon.wholeGameInfo,
  };
})(WeeklyReport);
